import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import { path, equals } from 'ramda';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { companyActions } from '../../../modules/company';
import useDetectDiffInChanges from '../../../utils/hooks/useDetectDiffInChanges';
import { useForm } from '../../../utils/hooks/useForm';
import useWindowSize from '../../../utils/hooks/useWindowSize.js';
import { DisplayFlexS } from '../../../utils/stylesHelpers/DispalyFlexS.js';
import EditPenIcon from '../../assets/imgs/icons/EditPenIcon.svg';
import { EMPLOYEE_TYPE } from '../../constants/company';
import { SEX_TYPES } from '../../constants/general';
import { SCREEN_SIZES } from '../../constants/screenSizes.js';
import { SelectS, Text, TextInput, Divider } from '../common';
import { ChangeLogoModal } from '../modals';

import SaveBtn from './common/saveBtn';
import {
  TeamsHeader,
  VerticalLine,
  AddNewFieldBtnWrapS,
  BlueCloseIcon,
  EmployeeAvatar,
  TeamsFormS,
  TeamsFormContentS,
  EditPenIconS,
  EmproyeeEditWrapperS,
  EmployeeSectionEditWrapper,
} from './companyEditS.js';

const { updateEmployees } = companyActions;

const EMPLOYEE_INITIAL_STATE = {
  avatar: '',
  firstName: '',
  lastName: '',
  jobTitle: '',
  email: '',
  education: '',
  sex: SEX_TYPES.MALE,
};

const validationSchema = Yup.object().shape({
  managers: Yup.array().of(
    Yup.object().shape({
      email: Yup.string().email(t('forms.common.emailForm.errors.isNotValid')),
    })
  ),
  otherStaff: Yup.array().of(
    Yup.object().shape({
      email: Yup.string().email(t('forms.common.emailForm.errors.isNotValid')),
    })
  ),
});

const EmployeeAvatarSection = ({
  avatar,
  employeeType,
  index,
  windowWidth,
  setEmployeeToUpdateAvatar,
  setIsLogoModalOpened,
}) => (
  <EmployeeAvatar
    avatar={typeof avatar === 'object' ? avatar.preview : avatar}
    onClick={() => {
      setIsLogoModalOpened(true);
      setEmployeeToUpdateAvatar({ type: employeeType, index });
    }}
  >
    {windowWidth <= SCREEN_SIZES.MEDIUM && (
      <EditPenIconS>
        <img src={EditPenIcon} alt="Edit avatar" />
      </EditPenIconS>
    )}
  </EmployeeAvatar>
);

const EmployeeFormSection = ({ employeeType, index, values, errors, touched, handleChange, handleBlur }) => {
  const INPUT_FIELDS_CONFIG = [
    { fieldName: 'firstName' },
    { fieldName: 'lastName' },
    { fieldName: 'jobTitle', placeholder: true },
    { fieldName: 'education', placeholder: true },
    { fieldName: 'email' },
  ];

  return INPUT_FIELDS_CONFIG.map(({ fieldName, placeholder }) => (
    <div key={fieldName}>
      <TextInput
        label={t(`companyEdit.teamSection.fieldLabels.${fieldName}`)}
        name={`${employeeType}[${index}].${fieldName}`}
        type="text"
        value={values[employeeType][index][fieldName]}
        placeholder={placeholder && t(`companyEdit.teamSection.fieldPlaceholders.${fieldName}`)}
        isError={path([employeeType, index, fieldName], errors) && path([employeeType, index, fieldName], touched)}
        error={path([employeeType, index, fieldName], errors)}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </div>
  ));
};

const AddEmployeeButton = ({ employeeType, addEmployee }) => (
  <AddNewFieldBtnWrapS
    className="employees"
    onClick={() => addEmployee(employeeType)}
  >
    <Text color="red">
      <FontAwesomeIcon icon="plus-circle" />
    </Text>
    <Text>{t('companyEdit.teamSection.addEmployeeBtn')}</Text>
  </AddNewFieldBtnWrapS>
);

const TeamSettings = ({ setActiveSettingType }) => {
  const [isLogoModalOpened, setIsLogoModalOpened] = useState(false);
  const [employeeToUpdateAvatar, setEmployeeToUpdateAvatar] = useState(null);
  const loading = useSelector((state) => state.company.loading);
  const company = useSelector((state) => state.company.companyItem);
  const dispatch = useDispatch();
  const { width: windowWidth } = useWindowSize();

  const getInitialStateFor = (type) => {
    const employeesByType = (company.employees || []).filter(({ employeeType }) => employeeType === type);
    return employeesByType.length ? employeesByType : [{ ...EMPLOYEE_INITIAL_STATE, employeeType: type }];
  };

  const initialValues = {
    managers: getInitialStateFor(EMPLOYEE_TYPE.MANAGER),
    otherStaff: getInitialStateFor(EMPLOYEE_TYPE.STAFF),
  };

  const handleSubmit = (values) => {
    const isEmployeeFilled = ({ email, firstName, lastName, education, jobTitle }) =>
      !!email || !!firstName || !!lastName || !!education || !!jobTitle;

    const employees = [...values.managers.filter(isEmployeeFilled), ...values.otherStaff.filter(isEmployeeFilled)];

    if (
      employees.length &&
      company.employees &&
      (!equals(company.employees, employees) || (company.employees.length && !employees.length))
    ) {
      dispatch(updateEmployees(employees));
    }
  };

  useEffect(() => {
    if (!isLogoModalOpened) {
      setEmployeeToUpdateAvatar(null);
    }
  }, [isLogoModalOpened]);

  const {
    values,
    errors,
    touched,
    onChange: handleChange,
    onBlur: handleBlur,
    onSubmit,
    setFieldValue,
    dirty,
  } = useForm({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const hasChanges = (currentValues) => {
    const initial = {
      managers: getInitialStateFor(EMPLOYEE_TYPE.MANAGER),
      otherStaff: getInitialStateFor(EMPLOYEE_TYPE.STAFF),
    };
    return !equals(initial, currentValues);
  };

  useDetectDiffInChanges(() => hasChanges(values), onSubmit, setActiveSettingType);

  const updateAvatar = (event) => {
    const target = event.target;
    const formData = new FormData();
    const reader = new FileReader();

    formData.append('avatar', target.files[0]);

    reader.onload = () => {
      const updatedEmployees = values[employeeToUpdateAvatar.type].map((employee, i) =>
        i === employeeToUpdateAvatar.index
          ? { ...employee, avatar: { preview: reader.result, data: formData } }
          : employee
      );
      setFieldValue(employeeToUpdateAvatar.type, updatedEmployees);
      setIsLogoModalOpened(false);
    };
    reader.readAsDataURL(target.files[0]);
  };

  const removeAvatar = () => {
    const updatedEmployees = values[employeeToUpdateAvatar.type].map((employee, i) =>
      i === employeeToUpdateAvatar.index ? { ...employee, avatar: '' } : employee
    );
    setFieldValue(employeeToUpdateAvatar.type, updatedEmployees);
    setIsLogoModalOpened(false);
  };

  const addEmployee = (employeeType) => {
    const newEmployee = {
      ...EMPLOYEE_INITIAL_STATE,
      employeeType: employeeType === 'managers' ? EMPLOYEE_TYPE.MANAGER : EMPLOYEE_TYPE.STAFF,
    };
    setFieldValue(employeeType, [...values[employeeType], newEmployee]);
  };

  const removeEmployee = (employeeType, index) => {
    const updatedEmployees = values[employeeType].filter((_, i) => i !== index);
    setFieldValue(employeeType, updatedEmployees);
  };

  return (
    <TeamsFormS onSubmit={onSubmit}>
      <ChangeLogoModal
        removeLogo={removeAvatar}
        updateLogo={updateAvatar}
        isModalOpen={isLogoModalOpened}
        toggleModal={setIsLogoModalOpened}
      />

      <TeamsFormContentS>
        <EmployeeSectionEditWrapper>
          <TeamsHeader>{t('companyEdit.teamSection.managersTitle')}</TeamsHeader>
          {values.managers.map((employee, i) => (
            <EmproyeeEditWrapperS key={i}>
              <DisplayFlexS justify="space-between" justifyScreenMedium="space-between">
                <EmployeeAvatarSection
                  avatar={employee.avatar}
                  employeeType="managers"
                  index={i}
                  windowWidth={windowWidth}
                  setEmployeeToUpdateAvatar={setEmployeeToUpdateAvatar}
                  setIsLogoModalOpened={setIsLogoModalOpened}
                />
                {i >= 1 && <BlueCloseIcon onClick={() => removeEmployee('managers', i)} />}
              </DisplayFlexS>
              <SelectS
                name={`managers.${i}.sex`}
                label={t('companyEdit.teamSection.fieldLabels.sexType')}
                value={employee.sex}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                {Object.values(SEX_TYPES).map((type) => (
                  <option key={type} value={type}>
                    {t(`companyEdit.teamSection.sexTypes.${type}`)}
                  </option>
                ))}
              </SelectS>
              <EmployeeFormSection
                employeeType="managers"
                index={i}
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
              <Divider style={{ marginBottom: '10px' }} />
              {i + 1 === values.managers.length && (
                <AddEmployeeButton employeeType="managers" addEmployee={addEmployee} />
              )}
            </EmproyeeEditWrapperS>
          ))}
        </EmployeeSectionEditWrapper>
        {windowWidth > SCREEN_SIZES.MEDIUM && (
          <DisplayFlexS style={{ marginTop: '110px' }} basis="10%" justify="center">
            <VerticalLine />
          </DisplayFlexS>
        )}
        <EmployeeSectionEditWrapper>
          <TeamsHeader>{t('companyEdit.teamSection.othersStaffTitle')}</TeamsHeader>
          {values.otherStaff.map((employee, i) => (
            <EmproyeeEditWrapperS key={i}>
              <DisplayFlexS justify="space-between" justifyScreenMedium="space-between">
                <EmployeeAvatarSection
                  avatar={employee.avatar}
                  employeeType="otherStaff"
                  index={i}
                  windowWidth={windowWidth}
                  setEmployeeToUpdateAvatar={setEmployeeToUpdateAvatar}
                  setIsLogoModalOpened={setIsLogoModalOpened}
                />
                {i >= 1 && <BlueCloseIcon onClick={() => removeEmployee('otherStaff', i)} />}
              </DisplayFlexS>
              <SelectS
                name={`otherStaff.${i}.sex`}
                label={t('companyEdit.teamSection.fieldLabels.sexType')}
                value={employee.sex}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                {Object.values(SEX_TYPES).map((type) => (
                  <option key={type} value={type}>
                    {t(`companyEdit.teamSection.sexTypes.${type}`)}
                  </option>
                ))}
              </SelectS>
              <EmployeeFormSection
                employeeType="otherStaff"
                index={i}
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
              <Divider style={{ marginBottom: '10px' }} />
              {i + 1 === values.otherStaff.length && (
                <AddEmployeeButton employeeType="otherStaff" addEmployee={addEmployee} />
              )}
            </EmproyeeEditWrapperS>
          ))}
        </EmployeeSectionEditWrapper>
      </TeamsFormContentS>

      <SaveBtn loading={loading} disabled={!dirty && !hasChanges(values)} />
    </TeamsFormS>
  );
};

export default TeamSettings;
